import { useTranslation } from 'react-i18next';

/**
 * Enterprise-grade hook for import translations with type safety and error handling
 * Provides centralized access to all import-related translations
 */
export function useImportTranslations() {
  // Use the default translation namespace since imports.json is merged into translation
  const { t } = useTranslation('translation');

  // Helper functions for common translation patterns with type safety
  const getErrorMessage = (errorKey: string, params?: Record<string, any>): string => {
    return String(t(`imports.errors.${errorKey}`, params || {}));
  };

  const getSuccessMessage = (successKey: string, params?: Record<string, any>): string => {
    return String(t(`imports.success.${successKey}`, params || {}));
  };

  const getWarningMessage = (warningKey: string, params?: Record<string, any>): string => {
    return String(t(`imports.warnings.${warningKey}`, params || {}));
  };

  const getWizardText = (section: string, key: string, params?: Record<string, any>): string => {
    return String(t(`imports.wizard.${section}.${key}`, params || {}));
  };

  const getModeText = (mode: string, key: string, params?: Record<string, any>): string => {
    return String(t(`imports.modes.${mode}.${key}`, params || {}));
  };

  const getMappingText = (key: string, params?: Record<string, any>): string => {
    return String(t(`imports.mapping.${key}`, params || {}));
  };

  const getProgressText = (key: string, params?: Record<string, any>): string => {
    return String(t(`imports.progress.${key}`, params || {}));
  };

  const getUploadText = (key: string, params?: Record<string, any>): string => {
    return String(t(`imports.upload.${key}`, params || {}));
  };

  const getTooltipText = (field: string): string => {
    return String(t(`imports.tooltips.${field}`, { defaultValue: '' }));
  };

  const getButtonText = (button: string): string => {
    return String(t(`imports.buttons.${button}`));
  };

  const getConfirmationText = (key: string, params?: Record<string, any>): string => {
    return String(t(`imports.confirmations.${key}`, params || {}));
  };

  const getStructureTypeText = (key: string, params?: Record<string, any>): string => {
    return String(t(`imports.structureTypes.${key}`, params || {}));
  };

  // Safe translation wrapper that always returns string
  const tSafe = (key: string, params?: Record<string, any>): string => {
    return String(t(key, params || {}));
  };

  // Specific error handlers for common import scenarios
  const getUploadError = (errorType: string, params?: Record<string, any>) => {
    return {
      title: getErrorMessage(`upload.${errorType}`, params),
      description:
        getErrorMessage(`upload.${errorType}Desc`, params) ||
        getErrorMessage(`upload.${errorType}`, params),
    };
  };

  const getMappingError = (errorType: string, params?: Record<string, any>) => {
    return {
      title: getErrorMessage(`mapping.${errorType}`, params),
      description:
        getErrorMessage(`mapping.${errorType}Desc`, params) ||
        getErrorMessage(`mapping.${errorType}`, params),
    };
  };

  const getImportError = (errorType: string, params?: Record<string, any>) => {
    return {
      title: getErrorMessage(`import.${errorType}`, params),
      description:
        getErrorMessage(`import.${errorType}Desc`, params) ||
        getErrorMessage(`import.${errorType}`, params),
    };
  };

  const getGeneralError = (errorType: string, params?: Record<string, any>) => {
    return {
      title: getErrorMessage(`general.${errorType}`, params),
      description:
        getErrorMessage(`general.${errorType}Desc`, params) ||
        getErrorMessage(`general.${errorType}`, params),
    };
  };

  // Helper for structured error objects with fallbacks
  const getStructuredError = (
    category: 'upload' | 'mapping' | 'import' | 'general',
    errorType: string,
    params?: Record<string, any>
  ) => {
    const baseKey = `imports.errors.${category}.${errorType}`;
    const descKey = `${baseKey}Desc`;

    return {
      title: String(t(baseKey, params || {})),
      description: String(
        t(descKey, {
          ...params,
          defaultValue: String(t(baseKey, params || {})),
        })
      ),
    };
  };

  // Progress message helpers
  const getProgressMessage = (processed: number, total: number, errors: number = 0): string => {
    if (errors > 0) {
      return (
        getProgressText('records.processed', { count: processed, total }) +
        ' - ' +
        getProgressText('records.errors', { count: errors })
      );
    }
    return getProgressText('records.processed', { count: processed, total });
  };

  const getCompletionMessage = (
    imported: number,
    errors: number = 0,
    skipped: number = 0
  ): string => {
    if (errors > 0 || skipped > 0) {
      return getWarningMessage('partialSuccessDesc', {
        imported,
        errors,
        skipped,
      });
    }
    return getSuccessMessage('importCompletedDesc', { count: imported });
  };

  // Field-specific tooltip helper
  const getFieldTooltip = (fieldId: string): string => {
    const tooltip = getTooltipText(fieldId);
    return tooltip || t('imports.mapping.fieldType', { field: fieldId });
  };

  return {
    // Direct translation function with type safety
    t: tSafe,

    // Category-specific helpers
    getErrorMessage,
    getSuccessMessage,
    getWarningMessage,
    getWizardText,
    getModeText,
    getMappingText,
    getProgressText,
    getUploadText,
    getTooltipText,
    getButtonText,
    getConfirmationText,
    getStructureTypeText,

    // Structured error helpers
    getUploadError,
    getMappingError,
    getImportError,
    getGeneralError,
    getStructuredError,

    // Progress and completion helpers
    getProgressMessage,
    getCompletionMessage,
    getFieldTooltip,
  };
}

export type ImportTranslations = ReturnType<typeof useImportTranslations>;
